/**
 * Admin Dashboard - Overview
 *
 * Shows quick stats, alerts (orders, contact messages, promos ending soon), and actions for Tammy.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  HiOutlineClipboardList,
  HiOutlineTag,
  HiOutlinePhotograph,
  HiOutlineCog,
  HiArrowRight,
  HiOutlineExternalLink,
  HiChevronDown,
  HiChevronUp,
  HiCheckCircle,
  HiOutlineBell,
  HiOutlineInbox,
} from 'react-icons/hi';

import { addCalendarDays, calendarDaysFromToday, localYyyyMmDd } from '../../utils/calendarDate';
import { adminFetch } from '../lib/adminApi';

interface AlertItem {
  id: string;
  type: 'orders' | 'messages' | 'promo';
  label: string;
  href: string;
  count?: number;
}

// Quick actions
const quickActions = [
  { label: 'Contact Messages', href: '/admin/messages', icon: HiOutlineInbox, color: 'blue' },
  { label: 'View Orders', href: '/admin/orders', icon: HiOutlineClipboardList, color: 'purple' },
  { label: 'Add Menu Item', href: '/admin/menu/new', icon: HiOutlineTag, color: 'pink' },
  { label: 'Settings', href: '/admin/settings', icon: HiOutlineCog, color: 'gray' },
];

// Color classes
const colorClasses = {
  purple: { bg: 'bg-purple-50', icon: 'text-purple-500' },
  pink: { bg: 'bg-pink-50', icon: 'text-pink-500' },
  blue: { bg: 'bg-blue-50', icon: 'text-blue-500' },
  gray: { bg: 'bg-gray-50', icon: 'text-gray-500' },
};

// localStorage key for integration status (same as Integrations page)
const INTEGRATION_STATUS_KEY = 'integration_status';

// Get integration status from localStorage
function getIntegrationStatus(): Record<string, 'connected' | 'not_connected'> {
  try {
    const stored = localStorage.getItem(INTEGRATION_STATUS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Invalid JSON, ignore
  }
  return {};
}

// Check if a task is completed
function isTaskCompleted(taskId: string): boolean {
  const status = getIntegrationStatus();
  // Google Business Profile is marked as 'connected' in Integrations.tsx by default (line 52)
  // Check localStorage first - if not set, default to 'connected' for google-business
  if (taskId === 'google-business') {
    // If explicitly set to 'not_connected' in localStorage, respect that
    if (status['google-business'] === 'not_connected') {
      return false;
    }
    // Otherwise, default to connected (matches Integrations.tsx default)
    return status['google-business'] === 'connected' || status['google-business'] === undefined;
  }
  return status[taskId] === 'connected';
}

export default function Dashboard() {
  const [expandedTask, setExpandedTask] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<AlertItem[]>([]);
  const [showGettingStarted, setShowGettingStarted] = useState(false);
  const [showWhatsLeft, setShowWhatsLeft] = useState(false);

  const loadAlerts = useCallback(async () => {
    const list: AlertItem[] = [];
    try {
      const [ordersRes, contactRes, promosRes] = await Promise.all([
        adminFetch('/api/orders/stats'),
        adminFetch('/api/contact/stats'),
        adminFetch('/api/promos'),
      ]);
        if (ordersRes.ok) {
          const d = await ordersRes.json();
          const pending = d.pending ?? 0;
          if (pending > 0) {
            list.push({
              id: 'orders',
              type: 'orders',
              label: pending === 1 ? '1 new order' : `${pending} new orders`,
              href: '/admin/orders',
              count: pending,
            });
          }
        }
        if (contactRes.ok) {
          const d = await contactRes.json();
          const newCount = d.new ?? 0;
          if (newCount > 0) {
            list.push({
              id: 'messages',
              type: 'messages',
              label: newCount === 1 ? '1 contact message' : `${newCount} contact messages`,
              href: '/admin/messages',
              count: newCount,
            });
          }
        }
        if (promosRes.ok) {
          const d = await promosRes.json();
          const promos = d.promos ?? d ?? [];
          const today = localYyyyMmDd();
          const weekEnd = addCalendarDays(today, 7);
          for (const p of promos) {
            if (!p.end_date || !p.is_active) continue;
            const endPart = String(p.end_date).split('T')[0];
            if (!/^\d{4}-\d{2}-\d{2}$/.test(endPart)) continue;
            if (endPart < today || endPart > weekEnd) continue;
            const days = Math.max(0, calendarDaysFromToday(endPart));
            list.push({
              id: `promo-${p.id}`,
              type: 'promo',
              label: `"${p.title || 'Promo'}" ends in ${days} day${days === 1 ? '' : 's'}`,
              href: '/admin/promos',
            });
          }
        }
      } catch {
        // Non-fatal
      }
      setAlerts(list);
  }, []);

  useEffect(() => {
    loadAlerts();
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, [loadAlerts]);

  // Get current time greeting
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 18) return 'Good afternoon';
    return 'Good evening';
  };

  const toggleTask = (taskId: string) => {
    setExpandedTask(expandedTask === taskId ? null : taskId);
  };

  return (
    <div className="space-y-8">
      {/* Welcome Header */}
      <div className="bg-gradient-to-r from-purple-600 to-pink-500 rounded-2xl p-6 text-white">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">{getGreeting()}, Tammy! 👋</h1>
            <p className="text-purple-100 mt-1">
              Welcome to your bakery dashboard. Manage your menu, discounts, and more.
            </p>
          </div>
          <a
            href="https://nannybbakery.ca"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 hover:bg-white/30 
                       rounded-lg text-white font-medium transition-colors whitespace-nowrap"
          >
            <HiOutlineExternalLink className="w-5 h-5" />
            View Live Site
          </a>
        </div>
      </div>

      {/* Alerts - things that need attention */}
      {alerts.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-amber-900 mb-3 flex items-center gap-2">
            <HiOutlineBell className="w-5 h-5" />
            Need attention
          </h2>
          <ul className="space-y-2">
            {alerts.map((a) => (
              <li key={a.id}>
                <Link
                  to={a.href}
                  className="flex items-center justify-between gap-2 py-2 px-3 rounded-lg bg-white border border-amber-100 hover:border-amber-300 hover:shadow-sm transition-colors"
                >
                  <span className="text-sm font-medium text-gray-800">{a.label}</span>
                  <HiArrowRight className="w-4 h-4 text-amber-600 flex-shrink-0" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick Actions */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {quickActions.map(action => {
            const colors = colorClasses[action.color as keyof typeof colorClasses];
            return (
              <Link
                key={action.label}
                to={action.href}
                className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl border 
                           border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
              >
                <div className={`p-3 rounded-full ${colors.bg}`}>
                  <action.icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <span className="text-sm font-medium text-gray-700 text-center">{action.label}</span>
              </Link>
            );
          })}
        </div>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Business Hours */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            📅 Your Business Hours
          </h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Tuesday - Friday</span>
              <span className="font-medium">11:00 AM - 4:30 PM</span>
            </div>
            <div className="flex justify-between py-2 border-b border-gray-100">
              <span className="text-gray-600">Saturday</span>
              <span className="font-medium">10:00 AM - 3:00 PM</span>
            </div>
            <div className="flex justify-between py-2">
              <span className="text-gray-600">Sunday - Monday</span>
              <span className="text-gray-400">Closed</span>
            </div>
          </div>
          <Link 
            to="/admin/settings"
            className="mt-4 inline-flex items-center gap-1 text-sm text-purple-600 hover:text-purple-700"
          >
            Edit hours <HiArrowRight className="w-4 h-4" />
          </Link>
        </div>

        {/* Getting Started Checklist — collapsed by default; expand when onboarding */}
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl border border-amber-100 overflow-hidden">
          <button
            type="button"
            onClick={() => setShowGettingStarted((v) => !v)}
            className="w-full p-5 flex items-center justify-between text-left hover:bg-amber-50/80 transition-colors"
          >
            <h3 className="font-semibold text-gray-900">🚀 Getting Started</h3>
            {showGettingStarted ? (
              <HiChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
            ) : (
              <HiChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
            )}
          </button>
          {showGettingStarted && (
          <ul className="px-5 pb-5 space-y-3">
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm">✓</span>
              <span className="text-sm text-gray-700">Menu items added</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm">✓</span>
              <span className="text-sm text-gray-700">Gallery photos uploaded</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm">✓</span>
              <span className="text-sm text-gray-700">Product feeds ready (Google & Facebook)</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm">✓</span>
              <span className="text-sm text-gray-700">WhatsApp & Messenger buttons added</span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm">✓</span>
              <span className="text-sm text-gray-700">Social sharing buttons added</span>
            </li>
          </ul>
          )}
        </div>
      </div>

      {/* What's Left To Do — collapsed by default; setup tasks still available */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowWhatsLeft((v) => !v)}
          className="w-full p-5 flex items-center justify-between text-left hover:bg-blue-50/80 transition-colors"
        >
          <div>
            <h3 className="font-semibold text-gray-900">📋 What&apos;s Left To Do</h3>
            <p className="text-xs text-gray-500 mt-1">Optional setup — expand when you&apos;re ready</p>
          </div>
          {showWhatsLeft ? (
            <HiChevronUp className="w-5 h-5 text-gray-500 flex-shrink-0" />
          ) : (
            <HiChevronDown className="w-5 h-5 text-gray-500 flex-shrink-0" />
          )}
        </button>
        {showWhatsLeft && (
        <>
        <p className="text-xs text-gray-500 px-5 -mt-2 mb-4">Click each task to see step-by-step instructions. Call Humberto when ready!</p>
        <ul className="px-5 pb-5 space-y-3">
          
          {/* 1. Google Business Profile */}
          {(() => {
            const isCompleted = isTaskCompleted('google-business');
            return (
              <li className={`bg-white rounded-lg border overflow-hidden ${
                isCompleted ? 'border-green-200 bg-green-50/30' : 'border-gray-200'
              }`}>
                <button 
                  onClick={() => toggleTask('google-business')}
                  className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isCompleted ? (
                      <span className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm flex-shrink-0">
                        <HiCheckCircle className="w-4 h-4" />
                      </span>
                    ) : (
                      <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm flex-shrink-0">1</span>
                    )}
                    <div className="text-left">
                      <p className={`font-medium ${isCompleted ? 'text-green-700' : 'text-gray-900'}`}>
                        {isCompleted ? '✓ ' : ''}Set up Google Business Profile
                      </p>
                      <p className="text-xs text-gray-500">Show your bakery on Google Maps & get reviews</p>
                    </div>
                  </div>
                  {expandedTask === 'google-business' ? (
                    <HiChevronUp className="w-5 h-5 text-gray-400" />
                  ) : (
                    <HiChevronDown className="w-5 h-5 text-gray-400" />
                  )}
                </button>
                {expandedTask === 'google-business' && (
                  <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                    <div className="pt-4">
                      {isCompleted && (
                        <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                          <p className="text-sm text-green-700 font-medium flex items-center gap-2">
                            <HiCheckCircle className="w-4 h-4" />
                            ✅ This task is completed! Great job!
                          </p>
                        </div>
                      )}
                      <h4 className="font-medium text-gray-900 mb-3">📍 Step-by-Step Instructions:</h4>
                      <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                        <li>Go to <a href="https://business.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">business.google.com</a></li>
                        <li>Click "Manage now" and sign in with your Google account</li>
                        <li>Search for "Nanny B's Bakery" - if it exists, claim it</li>
                        <li>If not found, click "Add your business to Google"</li>
                        <li>Enter your business name: <strong>Nanny B's Bakery</strong></li>
                        <li>Select category: <strong>Bakery</strong></li>
                        <li>Add your address: <strong>62 Commerce Park Drive, Unit L, Barrie, ON</strong></li>
                        <li>Add phone: <strong>(705) 503-4966</strong></li>
                        <li>Add website: <strong>https://nannybbakery.ca</strong></li>
                        <li>Set your hours (Tue-Fri 11am-4:30pm, Sat 10am-3pm)</li>
                        <li>Verify ownership (Google will send a postcard or call)</li>
                        <li>Upload photos of your bakery and products!</li>
                      </ol>
                      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <p className="text-sm text-green-700">
                          <strong>✨ Benefit:</strong> Customers searching "bakery near me" will find you!
                        </p>
                      </div>
                      <a 
                        href="https://business.google.com" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                      >
                        <HiOutlineExternalLink className="w-4 h-4" />
                        Open Google Business
                      </a>
                    </div>
                  </div>
                )}
              </li>
            );
          })()}

          {/* 2. Google Analytics */}
          <li className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button 
              onClick={() => toggleTask('google-analytics')}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm flex-shrink-0">2</span>
                <div className="text-left">
                  <p className="font-medium text-gray-900">Get Google Analytics ID</p>
                  <p className="text-xs text-gray-500">See how many people visit your website</p>
                </div>
              </div>
              {expandedTask === 'google-analytics' ? (
                <HiChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <HiChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedTask === 'google-analytics' && (
              <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                <div className="pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">📊 Step-by-Step Instructions:</h4>
                  <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                    <li>Go to <a href="https://analytics.google.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">analytics.google.com</a></li>
                    <li>Sign in with your Google account</li>
                    <li>Click "Start measuring" (or "Admin" if you have an account)</li>
                    <li>Account name: <strong>Nanny B's Bakery</strong></li>
                    <li>Property name: <strong>nannybbakery.ca</strong></li>
                    <li>Select your time zone and currency (CAD)</li>
                    <li>Choose "Web" as platform</li>
                    <li>Enter website URL: <strong>https://nannybbakery.ca</strong></li>
                    <li>Stream name: <strong>Nanny B's Website</strong></li>
                    <li>Click "Create stream"</li>
                    <li><strong>Copy the Measurement ID</strong> (starts with G-)</li>
                    <li>Send the ID to Humberto!</li>
                  </ol>
                  <div className="mt-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <p className="text-sm text-amber-700">
                      <strong>📋 What to send Humberto:</strong> The Measurement ID looks like <code className="bg-amber-100 px-1 rounded">G-ABC123XYZ</code>
                    </p>
                  </div>
                  <a 
                    href="https://analytics.google.com" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <HiOutlineExternalLink className="w-4 h-4" />
                    Open Google Analytics
                  </a>
                </div>
              </div>
            )}
          </li>

          {/* 3. Facebook Pixel */}
          <li className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <button 
              onClick={() => toggleTask('facebook-pixel')}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm flex-shrink-0">3</span>
                <div className="text-left">
                  <p className="font-medium text-gray-900">Set up Facebook Pixel (for ads)</p>
                  <p className="text-xs text-gray-500">Track visitors & run Facebook/Instagram ads</p>
                </div>
              </div>
              {expandedTask === 'facebook-pixel' ? (
                <HiChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <HiChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedTask === 'facebook-pixel' && (
              <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                <div className="pt-4">
                  <h4 className="font-medium text-gray-900 mb-3">📱 Step-by-Step Instructions:</h4>
                  <ol className="space-y-2 text-sm text-gray-600 list-decimal list-inside">
                    <li>Go to <a href="https://business.facebook.com/events_manager" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Meta Events Manager</a></li>
                    <li>Sign in with your Facebook account</li>
                    <li>Click the green "+ Connect Data Sources" button</li>
                    <li>Select "Web"</li>
                    <li>Choose "Meta Pixel" and click "Connect"</li>
                    <li>Name your pixel: <strong>Nanny B's Bakery Pixel</strong></li>
                    <li>Enter website: <strong>https://nannybbakery.ca</strong></li>
                    <li>Click "Continue"</li>
                    <li>Choose "Install code manually" (Humberto will handle this)</li>
                    <li><strong>Copy the Pixel ID</strong> (just the numbers)</li>
                    <li>Send the Pixel ID to Humberto!</li>
                  </ol>
                  <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700">
                      <strong>📋 What to send Humberto:</strong> The Pixel ID looks like <code className="bg-blue-100 px-1 rounded">123456789012345</code>
                    </p>
                  </div>
                  <div className="mt-3 p-3 bg-green-50 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700">
                      <strong>✨ Benefit:</strong> Run Facebook/Instagram ads that target people who visited your site!
                    </p>
                  </div>
                  <a 
                    href="https://business.facebook.com/events_manager" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm"
                  >
                    <HiOutlineExternalLink className="w-4 h-4" />
                    Open Meta Events Manager
                  </a>
                </div>
              </div>
            )}
          </li>

          {/* 4. Instagram Business (Optional) */}
          <li className="bg-white rounded-lg border border-gray-200 overflow-hidden opacity-60">
            <button 
              onClick={() => toggleTask('instagram')}
              className="w-full p-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
            >
              <div className="flex items-center gap-3">
                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 flex items-center justify-center text-sm flex-shrink-0">4</span>
                <div className="text-left">
                  <p className="font-medium text-gray-500">Convert to Instagram Business (Optional)</p>
                  <p className="text-xs text-gray-400">Show your Instagram feed on the website</p>
                </div>
              </div>
              {expandedTask === 'instagram' ? (
                <HiChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <HiChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </button>
            {expandedTask === 'instagram' && (
              <div className="px-4 pb-4 border-t border-gray-100 bg-gray-50">
                <div className="pt-4">
                  <p className="text-sm text-gray-500 mb-3">
                    <strong>Low priority</strong> - Do this later if you want your Instagram feed to show on the website.
                  </p>
                  <h4 className="font-medium text-gray-700 mb-3">📸 Instructions (when ready):</h4>
                  <ol className="space-y-2 text-sm text-gray-500 list-decimal list-inside">
                    <li>Open Instagram app on your phone</li>
                    <li>Go to your profile</li>
                    <li>Tap the menu (☰) in the top right</li>
                    <li>Tap "Settings and privacy"</li>
                    <li>Tap "Account type and tools"</li>
                    <li>Tap "Switch to professional account"</li>
                    <li>Choose "Business"</li>
                    <li>Connect to your Facebook Page</li>
                    <li>Done! Let Humberto know when complete.</li>
                  </ol>
                </div>
              </div>
            )}
          </li>
        </ul>
        </>
        )}
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Link
          to="/admin/menu"
          className="bg-purple-50 rounded-xl p-4 hover:shadow-md transition-shadow"
        >
          <HiOutlineTag className="w-6 h-6 text-purple-500 mb-2" />
          <p className="font-semibold text-gray-900">Menu</p>
          <p className="text-sm text-gray-500">Manage items</p>
        </Link>
        <Link
          to="/admin/orders"
          className="bg-amber-50 rounded-xl p-4 hover:shadow-md transition-shadow"
        >
          <HiOutlineClipboardList className="w-6 h-6 text-amber-500 mb-2" />
          <p className="font-semibold text-gray-900">Orders</p>
          <p className="text-sm text-gray-500">View orders</p>
        </Link>
        <Link
          to="/admin/messages"
          className="bg-blue-50 rounded-xl p-4 hover:shadow-md transition-shadow"
        >
          <HiOutlineInbox className="w-6 h-6 text-blue-500 mb-2" />
          <p className="font-semibold text-gray-900">Messages</p>
          <p className="text-sm text-gray-500">Contact form</p>
        </Link>
        <Link
          to="/admin/gallery"
          className="bg-pink-50 rounded-xl p-4 hover:shadow-md transition-shadow"
        >
          <HiOutlinePhotograph className="w-6 h-6 text-pink-500 mb-2" />
          <p className="font-semibold text-gray-900">Gallery</p>
          <p className="text-sm text-gray-500">Photo gallery</p>
        </Link>
      </div>

      {/* Help Footer */}
      <div className="text-center py-4 text-sm text-gray-500">
        Need help? Call Humberto at your convenience
      </div>
    </div>
  );
}
